import React from "react";

import { compact, filter, find, map, orderBy } from "lodash/fp";

import type {
  ACLResponse,
  ShareEntity,
  WorkspaceEntity,
} from "@app/common/types";
import type { EnterpriseWorkspace } from "@app/common/workspaces/types";

import ShareSuggestions, { Suggestion } from "@rui/ShareSuggestions";

const EMPTY_WORKSPACE_ENTITIES: WorkspaceEntity[] = [];

/**
 * Generates Workspace Entities for user's workspace/teams, and returns only those that
 * 1) have not already been shared with AND
 * 2) have not already been selected
 * */
const getSuggestedQuickShareEntities = (
  workspace: EnterpriseWorkspace,
  entityAccess: ACLResponse["entityAccess"],
  selectedEntities: ShareEntity[]
) => {
  if (!workspace.id) return EMPTY_WORKSPACE_ENTITIES;

  const selectedWorkspaceEntity = find(
    { type: "workspace", id: workspace.id },
    selectedEntities
  );
  const currentWorkspaceEntityAccess = find(
    { kind: "workspace", entityId: workspace.id },
    entityAccess
  );

  const workspaceEntity: WorkspaceEntity = {
    type: "workspace",
    id: workspace.id,
    workspaceId: workspace.id,
    name: workspace.name,
    isMutable: false,
    workspaceSize: workspace.users.length,
  };

  const currentTeamIdsWithAccess = map(
    "entityId",
    filter({ kind: "team" }, entityAccess)
  );

  const selectedTeamEntityIds = map(
    "id",
    filter(
      (entity) => typeof entity !== "string" && entity.type === "team",
      selectedEntities
    )
  );

  const teamEntities: WorkspaceEntity[] = compact(
    workspace.teams.map((team) => {
      if (
        currentTeamIdsWithAccess.includes(team.id) ||
        selectedTeamEntityIds.includes(team.id)
      ) {
        return null;
      }

      return {
        id: team.id,
        workspaceId: team.workspaceId,
        name: team.name,
        isMutable: false,
        type: "team",
        teamSize: team.users.length,
      };
    })
  );

  const workspaceEntityToInclude =
    currentWorkspaceEntityAccess || selectedWorkspaceEntity
      ? null
      : workspaceEntity;

  return compact([
    workspaceEntityToInclude,
    ...orderBy("name", "asc", teamEntities),
  ]);
};

const WorkspaceShareSuggestions = ({
  accessList,
  onAddEntity,
  selectedEntities,
  workspace,
}: {
  accessList?: ACLResponse;
  onAddEntity: (entity: WorkspaceEntity) => void;
  selectedEntities: ShareEntity[];
  workspace: EnterpriseWorkspace;
}) => {
  const suggestions = getSuggestedQuickShareEntities(
    workspace,
    accessList?.entityAccess ?? [],
    selectedEntities
  );

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    onAddEntity(suggestion as WorkspaceEntity);
  };

  return (
    <ShareSuggestions
      onSelectSuggestion={handleSelectSuggestion}
      suggestions={suggestions}
    />
  );
};

export default WorkspaceShareSuggestions;
